import { Entity, EntityType } from "@/queries";

// Union type for entity types that have versions
export type VersionedEntityType =
  | EntityType.PROMPT
  | EntityType.DATASET
  | EntityType.WORKFLOW
  | EntityType.SNIPPET
  | EntityType.TOOL;

// Type guard that narrows Entity to only versioned entity types
export const hasVersions = (entity: Entity): entity is Entity<VersionedEntityType> =>
  entity.type === EntityType.PROMPT ||
  entity.type === EntityType.DATASET ||
  entity.type === EntityType.WORKFLOW ||
  entity.type === EntityType.SNIPPET ||
  entity.type === EntityType.TOOL;

export type LabeledEntityType =
  | EntityType.PROMPT
  | EntityType.WORKFLOW
  | EntityType.SNIPPET
  | EntityType.TOOL;

export const hasReleaseLabels = (entity: Entity): entity is Entity<LabeledEntityType> =>
  entity.type === EntityType.PROMPT ||
  entity.type === EntityType.WORKFLOW ||
  entity.type === EntityType.SNIPPET ||
  entity.type === EntityType.TOOL;

/**
 * Creates a unique key for an entity that includes version/label information.
 * This allows the same entity to be selected multiple times with different variants.
 *
 * @param entity - The entity to create a key for
 * @param allowDuplicateVariants - Whether to include version/label in the key
 * @returns A unique string key for the entity variant
 */
export const getEntityVariantKey = (entity: Entity, allowDuplicateVariants = false): string => {
  const baseKey = `${entity.type}-${entity.id}`;

  // If duplicate variants are not allowed, return base key only
  if (!allowDuplicateVariants) {
    return baseKey;
  }

  // If entity has a label, include it in the key
  if (entity.label) {
    return `${baseKey}-l:${entity.label}`;
  }

  // If entity has a version number, include it in the key
  if (entity.version_number !== undefined) {
    return `${baseKey}-v:${entity.version_number}`;
  }

  // For entities without version/label, just use base key
  return baseKey;
};

/**
 * Checks if two entities represent the same variant (same id, type, version, and label).
 *
 * @param entity1 - First entity to compare
 * @param entity2 - Second entity to compare
 * @param allowDuplicateVariants - Whether to compare version/label
 * @returns true if the entities are the same variant
 */
export const isSameEntityVariant = (
  entity1: Entity,
  entity2: Entity,
  allowDuplicateVariants = false,
): boolean =>
  getEntityVariantKey(entity1, allowDuplicateVariants) ===
  getEntityVariantKey(entity2, allowDuplicateVariants);
